/* eslint-disable @typescript-eslint/no-explicit-any */
import { useQuery, useQueries } from "@tanstack/react-query";
import { Api } from "../../services/interceptor";
import { QUERY_KEY } from "../../services/queryEndPoint";
import { codeFor, colorFor, type MatchVm } from "../tournamentPage/matchVm";

export const norm = (s?: string | null) => (s ?? "").trim().toLowerCase();

/** URL of a team's page. Everything the page needs is rebuilt from these two ids, so the link can be shared. */
export const teamPath = (tournamentId: string, teamSnapshotId: string, tab?: string) =>
  `/tournamentDetails/${tournamentId}/teams/${teamSnapshotId}${tab ? `?tab=${tab}` : ""}`;
/** URL of the tournament's Teams tab. */
export const teamsTabPath = (tournamentId: string) => `/tournamentDetails/${tournamentId}?tab=teams`;

const unwrap = <T,>(data: any): T => (data && typeof data === "object" && "data" in data && !Array.isArray(data) && data.data !== undefined && !("divisionResponseList" in data) ? data.data : data) as T;

const personName = (p: any): string => {
  if (!p || typeof p !== "object") return "";
  return [p.playerFirstName, p.playerLastName].filter(Boolean).join(" ").trim();
};

export type DivGroup = { id: string; name: string };
export type DivNode = { id: string; name: string; groups: DivGroup[] };
export type TeamItem = {
  snapshotId: string;
  teamId: string;
  name: string;
  code: string;
  image?: string;
  color: string;
  captain: string;
  players: number | null;
  divisionId: string;
  divisionName: string;
  groupId: string;
  groupName: string;
  canEdit: boolean;
  counts: { won: number; lost: number; tied: number };
};

/** Division > group > team tree from getDivisions/{tournamentId}, flattened into a node list and a team list. */
export const parseDivisions = (payload: any): { divisions: DivNode[]; teams: TeamItem[] } => {
  const list: any[] = Array.isArray(payload) ? payload : (payload?.divisionResponseList ?? payload?.divisionResponses ?? []);
  const divisions: DivNode[] = [];
  const teams: TeamItem[] = [];
  const seen = new Set<string>();
  const add = (t: any, div: any, group: any | null) => {
    const snapshotId = String(t.teamSnapshotId ?? t.teamSnapShotId ?? t.snapshotId ?? "").trim();
    const name = String(t.teamName ?? t.team?.teamName ?? "").trim();
    if (!name || (snapshotId && seen.has(snapshotId))) return;
    if (snapshotId) seen.add(snapshotId);
    const inner = t.team ?? {};
    teams.push({
      snapshotId,
      teamId: String(inner.teamId ?? t.teamId ?? "").trim(),
      name,
      code: codeFor(name, t.teamCode ?? inner.teamCode ?? inner.teamShortCode),
      image: t.imageUrl || inner.imageUrl || t.imageUri || undefined,
      color: colorFor(name),
      captain: personName(t.captainPlayer) || personName(inner.captainPlayer),
      players: typeof inner.totalPlayers === "number" ? inner.totalPlayers : null,
      divisionId: String(div.divisionId ?? ""),
      divisionName: String(div.divisionName ?? "").trim() || "Division",
      groupId: group ? String(group.divisionId ?? "") : "",
      groupName: group ? String(group.divisionName ?? "").trim() : "",
      canEdit: !!inner.canEditTeam,
      counts: { won: inner.winMatchesCount ?? 0, lost: inner.lossMatchesCount ?? 0, tied: inner.drawMatchesCount ?? 0 },
    });
  };
  list.forEach((d) => {
    const groups: DivGroup[] = (d.childDivisions ?? []).map((c: any) => ({ id: String(c.divisionId), name: String(c.divisionName ?? "Group") }));
    divisions.push({ id: String(d.divisionId), name: String(d.divisionName ?? "").trim() || "Division", groups });
    (d.teams ?? []).forEach((t: any) => add(t, d, null));
    (d.childDivisions ?? []).forEach((c: any) => (c.teams ?? []).forEach((t: any) => add(t, d, c)));
  });
  return { divisions, teams };
};

export const useTournamentTeams = (tournamentId?: string | null) =>
  useQuery({
    queryKey: ["tmTeams", tournamentId],
    enabled: !!tournamentId,
    staleTime: 30_000,
    queryFn: async () => parseDivisions((await Api.get(`${QUERY_KEY.getDivisions}/${tournamentId}`)).data),
  });

/**
 * Snapshot ids the signed-in user may edit. getDivisions never fills `canEditTeam`, so it is read per team from
 * teamSnapshot/details (true for the squad's team admin / captain / vice-captain while the roster is open, and for
 * league admins). Same query key as the team page, so opening a team reuses the answer.
 */
export const useEditableTeams = (snapshotIds: string[], enabled: boolean) => {
  const results = useQueries({
    queries: snapshotIds.map((ts) => ({
      queryKey: ["tgDetails", ts],
      enabled: enabled && !!ts,
      retry: false,
      staleTime: 30_000,
      queryFn: async () => unwrap<any>((await Api.get(`teamSnapshot/details/${ts}`)).data),
    })),
  });
  const ids = new Set<string>();
  results.forEach((q, i) => { if (q.data?.canEditTeam) ids.add(snapshotIds[i]); });
  return ids;
};

export type TeamRecord = { played: number; won: number; lost: number; tied: number; nr: number; form: string[]; rank?: number; points?: number; nrr?: number };

const toRecord = (r: any): TeamRecord => ({
  played: r.matches ?? 0,
  won: r.won ?? 0,
  lost: r.lost ?? 0,
  tied: (r.tie ?? 0) + (r.draw ?? 0),
  nr: r.noResult ?? 0,
  form: Array.isArray(r.lastMatchesResult) ? r.lastMatchesResult.map((x: any) => String(x).toUpperCase()) : [],
  rank: r.rank,
  points: r.points,
  nrr: r.netRunrate,
});

/**
 * A team's record from its finished matches (every stage, playoffs included), so it always agrees with the team's
 * Matches tab. The points table only counts league fixtures, so it is the fallback while matches load.
 */
export const recordFromMatches = (vms: MatchVm[], snapshotId: string, name: string): TeamRecord | null => {
  const mine = vms.filter((m) => m.bucket === "past" && [m.raw?.teamOne, m.raw?.teamTwo].some((t: any) => (t?.teamSnapshotId && t.teamSnapshotId === snapshotId) || (!t?.teamSnapshotId && norm(t?.teamName) === norm(name))));
  if (mine.length === 0) return null;
  const rec: TeamRecord = { played: mine.length, won: 0, lost: 0, tied: 0, nr: 0, form: [] };
  [...mine].sort((a, b) => b.ms - a.ms).forEach((m) => {
    const side = norm(m.a.name) === norm(name) ? m.a : m.b;
    const r = m.outcome === "NR" ? "NR" : m.outcome === "T" ? "T" : side.won ? "W" : side.lost ? "L" : "NR";
    if (r === "W") rec.won++; else if (r === "L") rec.lost++; else if (r === "T") rec.tied++; else rec.nr++;
    rec.form.push(r);
  });
  return rec;
};

/** Played / won / lost / tied and last-5 form per team (by teamSnapshotId, and by name as a fallback) from the points tables. */
export const useTeamRecords = (divisionIds: string[]) => {
  const results = useQueries({
    queries: divisionIds.map((id) => ({
      queryKey: ["tmPoints", id],
      staleTime: 30_000,
      queryFn: async () => ((await Api.get(`${QUERY_KEY.getCombinedPointsTableByDivision}/${id}`)).data ?? {}) as Record<string, any[]>,
    })),
  });
  const byId = new Map<string, TeamRecord>();
  const byName = new Map<string, TeamRecord>();
  results.forEach((q) =>
    Object.values(q.data ?? {}).forEach((rows) =>
      (Array.isArray(rows) ? rows : []).forEach((r) => {
        const rec = toRecord(r);
        if (r.teamSnapshotId) byId.set(String(r.teamSnapshotId), rec);
        if (r.teamSnapshotName) byName.set(norm(r.teamSnapshotName), rec);
      }),
    ),
  );
  return { byId, byName, loading: results.some((q) => q.isLoading) };
};

/** Snapshot ids and names of teams in a match that is live right now. */
export const useLiveTeams = (tournamentId?: string | null) =>
  useQuery({
    queryKey: ["tmLive", tournamentId],
    enabled: !!tournamentId,
    refetchInterval: 30_000,
    queryFn: async () => {
      const get = (endpoint: string) => Api.get(endpoint, { params: { tournamentId, status: "IN_PROGRESS", page: 0, size: 50 } });
      let res;
      try {
        res = await get(QUERY_KEY.matchSummaryByTournamentIdAndStatusPageable);
      } catch {
        res = await get(QUERY_KEY.matchByTournamentIdAndStatusPageable);
      }
      const ids = new Set<string>();
      const names = new Set<string>();
      ((res?.data?.matchResponseList ?? []) as any[]).forEach((m) =>
        [m.teamOne, m.teamTwo].forEach((t) => {
          if (t?.teamSnapshotId) ids.add(String(t.teamSnapshotId));
          if (t?.teamName) names.add(norm(t.teamName));
        }),
      );
      return { ids, names };
    },
  });

export const resultPill = (r: string): "W" | "L" | "T" | "NR" => {
  const u = r.toUpperCase();
  return u === "W" ? "W" : u === "L" ? "L" : u === "NR" ? "NR" : "T";
};

export type BatLine = { id: string; name: string; image?: string; mat: number; inn: number; no: number; runs: number; hs: number; ave: number; sr: number; c100: number; c50: number; f4: number; s6: number; teamSnapshotId: string };
export type BowlLine = { id: string; name: string; image?: string; mat: number; inn: number; overs: number; mdn: number; runs: number; wkts: number; econ: number; w5: number; teamSnapshotId: string };

const playerOf = (p: any) => ({ id: String(p?.playerId ?? ""), name: personName(p) || "Player", image: p?.imageUri || undefined });

/** The whole-tournament leaderboard (BATTING / BOWLING), reduced to one team's players. */
export const useTeamLeaderboard = (tournamentId: string | undefined, teamSnapshotId: string | undefined, enabled = true) => {
  const run = (filter: "BATTING" | "BOWLING") =>
    useQuery({
      queryKey: [QUERY_KEY.tournamentLeaderboard, tournamentId, filter],
      enabled: !!tournamentId && enabled,
      staleTime: 60_000,
      queryFn: async () => (await Api.get(QUERY_KEY.tournamentLeaderboard, { params: { tournamentId, filter } })).data,
    });
  const bat = run("BATTING");
  const bowl = run("BOWLING");
  const batting: BatLine[] = ((bat.data?.battingStats ?? []) as any[])
    .filter((e) => e?.teamSnapshot?.teamSnapshotId === teamSnapshotId)
    .map((e) => ({ ...playerOf(e.batsman), teamSnapshotId: teamSnapshotId as string, mat: e.totalMatchesCount ?? 0, inn: e.totalInningsCount ?? 0, no: e.notOutCount ?? 0, runs: e.totalRunsScored ?? 0, hs: e.highestScore ?? 0, ave: e.average ?? 0, sr: e.strikeRate ?? 0, c100: e.centuriesCount ?? 0, c50: e.halfCenturiesCount ?? 0, f4: e.foursCount ?? 0, s6: e.sixesCount ?? 0 }));
  const bowling: BowlLine[] = ((bowl.data?.bowlingStats ?? []) as any[])
    .filter((e) => e?.teamSnapshot?.teamSnapshotId === teamSnapshotId)
    .map((e) => ({ ...playerOf(e.bowler), teamSnapshotId: teamSnapshotId as string, mat: e.totalMatchesCount ?? 0, inn: e.totalInningsCount ?? 0, overs: Number(e.totalOvers ?? 0), mdn: e.totalMaidenOverCount ?? 0, runs: e.totalRunsCenceded ?? e.totalRunsConceded ?? 0, wkts: e.totalWickets ?? 0, econ: e.economy ?? 0, w5: e.fiveWicketsCount ?? 0 }));
  return { batting, bowling, loading: bat.isLoading || bowl.isLoading, error: bat.isError || bowl.isError };
};

export { unwrap };
